import { useEffect, useMemo, useState } from 'react';
import { BoxProps, Grid, Stack } from '@mui/material';
import { useTranslations } from 'next-intl';

import { BodyMedium, Center, TitleLarge } from '@dh/styles';
import { FormattedOutput, OutlinedButton, ProjectCard } from '@dh/components';
import { getBreakpointValue, Project } from '@dh/types';
import { getRandomElements } from '@dh/utils';
import { useBreakpoint, useNavigateTo, useTheme } from '@dh/hooks';

import { ProjectsContainer, ProjectsGrid } from './styles';
import { ROUTES } from '../../../../constants';

// Constants
const Settings = {
    MAX_PROJECTS: 3
} as const;

interface Props extends BoxProps {
    suggestableProjects: Project[];
}

const DashboardProjectsSection = ({ suggestableProjects, ...boxProps }: Props) => {
    const t = useTranslations('dashboard');
    const navigateTo = useNavigateTo();
    const breakpoint = useBreakpoint();
    const { colors } = useTheme();
    const [suggestions, setSuggestions] = useState(
        suggestableProjects.slice(0, Settings.MAX_PROJECTS)
    );

    useEffect(() => {
        setSuggestions(
            suggestableProjects.length > Settings.MAX_PROJECTS
                ? getRandomElements(suggestableProjects, Settings.MAX_PROJECTS)
                : suggestableProjects
        );
    }, [suggestableProjects]);

    // Responsive grid item widths for consistent layout across all grid screens
    // Compact (mobile): 100% - single column
    // Medium (tablet): 48.5% - two columns (slightly under 50% to account for gap)
    // Expanded (desktop): 360px - fixed optimal card width
    const gridItemStyle = useMemo(
        () => ({
            display: 'flex',
            width: getBreakpointValue(breakpoint, '100%', '48.5%', '360px'),
            alignItems: 'stretch'
        }),
        [breakpoint]
    );

    return (
        <ProjectsContainer px={3} py={5} {...boxProps}>
            <Stack mb={4} gap={1}>
                <TitleLarge color='white'>{t('recommended_projects.headline')}</TitleLarge>
                <FormattedOutput color='white'>{t('recommended_projects.description')}</FormattedOutput>
            </Stack>

            <TitleLarge mb={2} color='white'>
                {t('recommended_projects.title')}
            </TitleLarge>

            {suggestions.length === 0 && (
                <BodyMedium my={2} color='white'>
                    {t('already_seen_section_empty_text')}
                </BodyMedium>
            )}
            <ProjectsGrid container mb={3}>
                {suggestions.map((project) => (
                    <Grid key={project.id} style={gridItemStyle}>
                        <ProjectCard project={project} sx={{ height: '100%' }} />
                    </Grid>
                ))}
            </ProjectsGrid>
            <Center>
                <OutlinedButton
                    label={t('go_to_projects')}
                    onClick={() => navigateTo(ROUTES.Projects)}
                    labelColor={colors.primaryDark}
                />
            </Center>
        </ProjectsContainer>
    );
};

export { DashboardProjectsSection };
